import type { MessageContent, SlotOccurrence } from "../types"
import { parseMessageContent } from "../markup/conversion"
import { validateMessageContent } from "../markup/validation"
import { EntityContent } from "./entityContent"
import { MediaContent } from "./mediaContent"
import { previewLabel, resolvePreview } from "./presentation"
import "./telegramPreview.css"

export interface TelegramPreviewSlot {
  id: string
  label?: string
  /** Already resolved by the caller; core has no binding/catalog knowledge. */
  url?: string | null
}

export interface TelegramPreviewMedia {
  id: string
  kind: "photo" | "video"
  name: string
  /** Bytes, when known. */
  size?: number
  previewUrl?: string
  previewLoading?: boolean
  previewError?: string
  onPreviewError?: () => void
  onPreviewRetry?: () => void
}

export interface TelegramPreviewButtonClick {
  row: number
  index: number
  button: MessageContent["buttons"][number][number]
  url?: string
  occurrences: readonly SlotOccurrence[]
}

export interface TelegramMessagePreviewProps {
  value: MessageContent
  media?: readonly TelegramPreviewMedia[]
  slots?: readonly TelegramPreviewSlot[]
  /** Explicit fallback, not implicitly retained or enriched with current data. */
  lastGoodContent?: MessageContent | null
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  onButtonClick?: (button: TelegramPreviewButtonClick) => void
  /** Off by default; selecting context or handling a button always prevents navigation. */
  allowLinkNavigation?: boolean
  /** Optional informational text/time supplied by the caller, never live counts. */
  footer?: string
}

export function TelegramMessagePreview(props: TelegramMessagePreviewProps) {
  const current = parseMessageContent(props.value)
  const fallback =
    !current.ok && props.lastGoodContent
      ? parseMessageContent(props.lastGoodContent)
      : undefined
  const parsed = current.ok
    ? current.value
    : fallback?.ok
      ? fallback.value
      : undefined
  const content =
    !current.ok && fallback?.ok ? props.lastGoodContent! : props.value
  const stale = !current.ok
  // Stale source must not select occurrences from the current draft or acquire
  // new slot values. The caller explicitly owns both source and fallback.
  const presentation = parsed
    ? resolvePreview(content, parsed, stale ? [] : props.slots)
    : undefined
  const onSelectOccurrence = stale ? undefined : props.onSelectOccurrence
  const validity = validateMessageContent(props.value)
  const warnings = [
    ...validity.diagnostics
      .filter((item) => current.ok || item.severity === "warning")
      .map((item) =>
        item.code === "ALBUM_KEYBOARD"
          ? "Альбом не поддерживает клавиатуру. Кнопки сохранены в исходнике, но не показаны."
          : previewLabel(item.message),
      ),
    ...(presentation?.warnings ?? []),
  ]
  return (
    <section className="telegram-preview" aria-label="Предпросмотр Telegram">
      {!current.ok && (
        <div className="telegram-preview__warning" role="alert">
          {parsed
            ? "Предпросмотр устарел: показано последнее корректное содержимое."
            : "Предпросмотр недоступен: исправьте исходный текст."}
          {current.diagnostics.map((item, index) => (
            <div key={index}>{previewLabel(item.message)}</div>
          ))}
        </div>
      )}
      {!!warnings.length && (
        <div className="telegram-preview__warning" role="status">
          {warnings.map((warning, index) => (
            <div key={index}>{warning}</div>
          ))}
        </div>
      )}
      {presentation && (
        <div className="telegram-preview__message">
          <article className="telegram-preview__bubble">
            <MediaContent ids={content.mediaIds} media={props.media} />
            {!!presentation.text && (
              <div className="telegram-preview__text">
                <EntityContent
                  content={presentation}
                  onSelectOccurrence={onSelectOccurrence}
                  allowLinkNavigation={!stale && props.allowLinkNavigation}
                />
              </div>
            )}
            {props.footer && (
              <footer className="telegram-preview__footer">
                {previewLabel(props.footer)}
              </footer>
            )}
          </article>
          {content.mediaIds.length <= 1 && !!presentation.buttons.length && (
            <div
              className="telegram-preview__keyboard"
              aria-label="Клавиатура сообщения"
            >
              {presentation.buttons.map((row, rowIndex) => (
                <div className="telegram-preview__keyboard-row" key={rowIndex}>
                  {row.map((button, index) => {
                    const select = () => {
                      if (button.occurrence)
                        onSelectOccurrence?.(button.occurrence)
                      if (!stale)
                        props.onButtonClick?.({
                          row: rowIndex,
                          index,
                          button: content.buttons[rowIndex][index],
                          url: button.url,
                          occurrences: button.occurrences,
                        })
                    }
                    const navigate =
                      !stale &&
                      props.allowLinkNavigation &&
                      button.url &&
                      !props.onButtonClick &&
                      !(button.occurrence && onSelectOccurrence)
                    const missing =
                      button.missing ||
                      button.textSlots.some((slot) => slot.missing)
                    const className = `telegram-preview__keyboard-button${button.style ? ` telegram-preview__keyboard-button--${button.style}` : ""}${missing ? " telegram-preview__missing" : ""}`
                    const title = missing
                      ? "Не задано значение или небезопасный URL"
                      : undefined
                    return (
                      <div
                        className="telegram-preview__keyboard-item"
                        key={index}
                      >
                        {navigate ? (
                          <a
                            className={className}
                            title={title}
                            href={button.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {button.text}
                          </a>
                        ) : (
                          <button
                            type="button"
                            className={className}
                            title={title}
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              select()
                            }}
                          >
                            {button.text}
                          </button>
                        )}
                        {onSelectOccurrence &&
                          button.textSlots.map((slot, slotIndex) => (
                            <button
                              type="button"
                              key={slotIndex}
                              className="telegram-preview__context"
                              aria-label={`Выбрать ${slot.label}: текст кнопки ${rowIndex + 1}.${index + 1}`}
                              onClick={(event) => {
                                event.stopPropagation()
                                onSelectOccurrence(slot.occurrence)
                              }}
                            >
                              Текст: {slot.label}
                            </button>
                          ))}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
